import { useMemo, useEffect } from "react";
import { Box, Typography, Card, CardContent, Stack } from "@mui/material";
import { useParams, useOutletContext } from "react-router-dom";

import useGetConsent from "../../hooks/useGetConsents";

const fmt = (d) =>
  d
    ? new Date(d).toLocaleString([], {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : null;

const EconsentDetails = () => {
  const { _id_: patientId, caseId } = useParams();
  const { setActive, setBreadcrumbTitle } = useOutletContext();

  useEffect(() => {
    setActive?.("econsent");
    setBreadcrumbTitle?.("Econsent Details");
  }, [setActive, setBreadcrumbTitle]);

  const { data: consentData = [] } = useGetConsent({ patientId });

  // Find the row for this caseId (assuming caseId === trial_id)
  const selected = useMemo(
    () => consentData.find((x) => String(x.trial_id) === String(caseId)) || null,
    [consentData, caseId]
  );

  if (!selected) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          Consent not found for this case.
        </Typography>
      </Box>
    );
  }

  const title = selected.consent?.title ?? "";
  const subtitle = selected.consent?.subtitle ?? "";
  const content = selected.consent?.content ?? "";
  const signedAt = fmt(selected.consent_signed_date);
  const isSigned = !!selected.is_consent_signed;
  const trialName = selected.trial_name;

  return (
    <Box sx={{ p: 2 }}>
      <Stack spacing={1} sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, textAlign: "center" }}>
          {title}
        </Typography>

        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="subtitle1">{trialName}</Typography>

          <Stack spacing={0.5}>
            <Typography variant="subtitle1">{isSigned ? "Signed" : "Not Signed"}</Typography>
            {signedAt && (
              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                {signedAt}
              </Typography>
            )}
          </Stack>
        </Stack>

        {subtitle && (
          <Typography variant="subtitle2" sx={{ textAlign: "center", color: "text.secondary" }}>
            {subtitle}
          </Typography>
        )}
      </Stack>

      <Card variant="outlined" sx={{ borderRadius: 2, boxShadow: 0, borderColor: "divider" }}>
        <CardContent sx={{ p: 2 }}>
          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", lineHeight: 1.5 }}>
            {content}
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default EconsentDetails;
